import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Camera, X, RefreshCw, Monitor } from 'lucide-react';

export default function BarcodeScanner({ onScan, buttonLabel = 'Scan', buttonVariant = 'outline', buttonSize = 'icon', className = '' }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [cameras, setCameras] = useState([]);
  const [activeCameraIdx, setActiveCameraIdx] = useState(0);
  const [ready, setReady] = useState(false);
  const scannerRef = useRef(null);
  const scanningRef = useRef(false);
  const mountedRef = useRef(false);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && scanningRef.current) {
      scanningRef.current = false;
      try { await scannerRef.current.stop(); } catch {}
      try { scannerRef.current.clear(); } catch {}
    }
  }, []);

  const startScanner = useCallback(async (cameraId) => {
    setError('');
    setReady(false);

    // Stop any existing scanner first
    await stopScanner();

    // Small delay to let the DOM settle after stopping
    await new Promise(r => setTimeout(r, 300));

    if (!mountedRef.current) return;

    const readerEl = document.getElementById('barcode-reader');
    if (!readerEl) { setError('Scanner container not found.'); return; }

    try {
      const scanner = new Html5Qrcode('barcode-reader');
      scannerRef.current = scanner;

      const config = { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 1.5 };

      const onSuccess = (decodedText) => {
        onScan(decodedText);
        stopScanner();
        setOpen(false);
      };

      if (cameraId) {
        await scanner.start(cameraId, config, onSuccess, () => {});
      } else {
        // Try environment first, then user, then first available
        try {
          await scanner.start({ facingMode: 'environment' }, config, onSuccess, () => {});
        } catch {
          try {
            await scanner.start({ facingMode: 'user' }, config, onSuccess, () => {});
          } catch {
            throw new Error('no_camera');
          }
        }
      }
      scanningRef.current = true;
      setReady(true);
    } catch (err) {
      const msg = String(err?.message || err);
      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setError('Camera permission denied. Please allow camera access in your browser settings and try again.');
      } else if (msg.includes('NotFoundError') || msg.includes('no_camera') || msg.includes('Requested device not found')) {
        setError('No camera found on this device. Use the manual entry field or a USB barcode scanner instead.');
      } else if (msg.includes('NotReadableError') || msg.includes('Could not start')) {
        setError('Camera is in use by another app. Close other apps using the camera and try again.');
      } else {
        setError(`Camera error: ${msg}`);
      }
    }
  }, [onScan, stopScanner]);

  // Enumerate cameras when dialog opens
  useEffect(() => {
    if (!open) return;
    mountedRef.current = true;

    const init = async () => {
      try {
        const deviceList = await Html5Qrcode.getCameras();
        if (deviceList && deviceList.length > 0) {
          setCameras(deviceList);
          // Prefer environment/back camera
          const backIdx = deviceList.findIndex(d =>
            (d.label || '').toLowerCase().match(/back|rear|environment/)
          );
          const idx = backIdx >= 0 ? backIdx : 0;
          setActiveCameraIdx(idx);
          await startScanner(deviceList[idx].id);
        } else {
          setError('No cameras detected. Use the manual entry field or a USB barcode scanner.');
        }
      } catch (err) {
        const msg = String(err?.message || err);
        if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
          setError('Camera permission denied. Please allow camera access in your browser settings and try again.');
        } else {
          setError('Could not access cameras. Make sure you\'re using HTTPS and have granted camera permissions.');
        }
      }
    };

    // Delay slightly so the dialog DOM is rendered
    const timer = setTimeout(init, 200);

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      stopScanner();
    };
  }, [open, startScanner, stopScanner]);

  const handleClose = () => {
    stopScanner();
    setOpen(false);
    setError('');
    setCameras([]);
    setReady(false);
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    const nextIdx = (activeCameraIdx + 1) % cameras.length;
    setActiveCameraIdx(nextIdx);
    await startScanner(cameras[nextIdx].id);
  };

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        onClick={() => setOpen(true)}
        className={className}
        title="Scan barcode or QR code"
        data-testid="scan-barcode-btn"
      >
        <Camera className="h-4 w-4" />
        {buttonLabel !== 'Scan' && <span className="ml-1.5">{buttonLabel}</span>}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(true); }}>
        <DialogContent className="sm:max-w-md" data-testid="barcode-scanner-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" /> Scan Barcode / QR Code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Camera selector bar */}
            {cameras.length > 1 && (
              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5">
                <div className="flex items-center gap-1.5 text-xs text-gray-600 min-w-0">
                  <Monitor className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{cameras[activeCameraIdx]?.label || `Camera ${activeCameraIdx + 1}`}</span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={switchCamera} data-testid="switch-camera-btn">
                  <RefreshCw className="h-3 w-3 mr-1" /> Switch ({cameras.length})
                </Button>
              </div>
            )}

            {/* Scanner viewport */}
            <div
              id="barcode-reader"
              className="w-full rounded-lg overflow-hidden bg-black min-h-[280px]"
              data-testid="barcode-reader-viewport"
            />

            {/* Status */}
            {!error && !ready && (
              <p className="text-xs text-gray-500 text-center animate-pulse">Starting camera...</p>
            )}
            {ready && (
              <p className="text-xs text-emerald-600 text-center">Camera active — point at a barcode or QR code</p>
            )}

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg" data-testid="scanner-error">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="outline" onClick={handleClose} data-testid="close-scanner-btn">
                <X className="h-4 w-4 mr-1.5" /> Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
